import { useState } from "react";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { defineMessages, useIntl } from "react-intl";
import { classNames } from "@ct-react/core";
import { useLocaleNavigate } from "@ct-react/locale";
import { ArticleType, buildSearchUrl } from "@shared/url";
import { actions, articleTypes, common } from "../i18n/sharable-defs";
import { Wrapper } from "../components/layout/wrapper";
import Button from "../components/common/button";
import SeoMetas from "../components/layout/seo-metas";
import ArticleCard from "../components/article-card";
import EstimatePart from "../components/home/estimate";
import AboutPart from "../components/home/about";
import PrestationsPart from "../components/home/prestations";
import ReviewsPart from "../components/home/reviews";
import PageFoot from "../components/layout/page-elements/page-foot";
import footBackground from "../assets/home/footer-contact.png";
import "./home.scss";

const transDefs = defineMessages({
  title: { id: "homepage.title", defaultMessage: "Vente, location et rénovation dans les 4 Vallées" },
  subtitle: { id: "homepage.subtitle", defaultMessage: "Immobilier de prestige à Nendaz" },
  properties: { id: "homepage.properties-title", defaultMessage: "Une sélection de nos plus beaux biens du moment" },
  explore: { id: "homepage.properties-link", defaultMessage: "Explorer l'ensemble des biens" },
  foot: { id: "homepage.foot.title", defaultMessage: "Rencontrons-nous et parlez nous de vous et de votre projet !" },
  seo: { id: "homepage.seo-desc", defaultMessage: "EV Real Estate & Services agence immobilière indépendante spécialisées en achat/vente de chalets et appartements sur Haute-Nendaz, Verbier, La Tzoumaz et Veysonnaz." }
});

const SUGGESTIONS_GQL_DATA = gql`
  query HomeSuggestions($where: AnyArticleFilterInput) {
    search(where: $where, first: 8) {
      edges {
        cursor
        node {
          ... on RentalAccommodationArticle {
            id slug
            novelty recommended promoted
            images @limit(max: 10) { assets { url breakpoint } }
            address { city }
            title { value }
            lowestPrice { type onDemand value }
            features { maxGuests bedrooms bathrooms }
          }
          ... on SaleAccommodationArticle {
            id slug
            novelty recommended promoted sold
            images @limit(max: 10) { assets { url breakpoint } }
            address { city }
            title { value }
            price { onDemand value }
            features { rooms bedrooms bathrooms livingSpace plotSpace }
          }
        }
        accommodationBookingSuggestion {
          period bookable priceOnDemand
          price { amount detailed { bare discount providerCharge { amount adaptive } } }
        }
      }
      paginationInfo {
        currentPage pageCount pageSize hasNextPage hasPreviousPage
        lastPageCursor nextPageCursor previousPageCursor linkPageCursors { cursor page }
      }
    }
  }
`;

const HomePage = () => {

  const intl = useIntl();
  const navigate = useLocaleNavigate();

  const [ propertiesDisplay, setPropertiesDisplay ] = useState<ArticleType>(ArticleType.SALE);

  const { loading, data } = useQuery<any>(SUGGESTIONS_GQL_DATA, {
    ssr: false,
    variables: {
      ...(propertiesDisplay === ArticleType.SALE) && {
        where: { saleAccommodation: { sold: false } }
      },
      ...(propertiesDisplay === ArticleType.RENTAL) && {
        where: { rentalAccommodation: {} }
      }
    }
  });

  const onSearch = (type: ArticleType) => navigate(buildSearchUrl(type));

  return (
    <>

      <SeoMetas title={`${intl.formatMessage(transDefs.subtitle)} | EV Real Estate`}
                description={intl.formatMessage(transDefs.seo)} />

      <Wrapper.Centered as="section" className="hp hero">
        <div className="title-wrap">
          <span className="sub">{intl.formatMessage(transDefs.subtitle)}</span>
          <h1>{intl.formatMessage(transDefs.title)}</h1>
        </div>
        <div className="article-links">
          <Button type="button"
                  onClick={() => onSearch(ArticleType.SALE)}>
            {intl.formatMessage(articleTypes.nav[ArticleType.SALE])}
          </Button>
          <Button type="button"
                  onClick={() => onSearch(ArticleType.RENTAL)}>
            {intl.formatMessage(articleTypes.nav[ArticleType.RENTAL])}
          </Button>
        </div>
      </Wrapper.Centered>

      <Wrapper.ScrollableList className="hp separator properties content">
        <Wrapper.ScrollableListHeader title={intl.formatMessage(transDefs.properties)}
                                      subTitle={intl.formatMessage(common.ourProperties)}>
          <div className="p-selector">
            <Button type="button"
                    className={classNames("small", "outline", "cream", {selected: propertiesDisplay === ArticleType.SALE})}
                    onClick={() => setPropertiesDisplay(ArticleType.SALE)}>
              {intl.formatMessage(articleTypes.nav[ArticleType.SALE])}
            </Button>
            <Button type="button"
                    className={classNames("small", "outline", "cream", {selected: propertiesDisplay === ArticleType.RENTAL})}
                    onClick={() => setPropertiesDisplay(ArticleType.RENTAL)}>
              {intl.formatMessage(articleTypes.nav[ArticleType.RENTAL])}
            </Button>
          </div>
        </Wrapper.ScrollableListHeader>
        <Wrapper.ScrollableListContent key={propertiesDisplay}>
          {(data?.search.edges || Array.from({length: 3})).map((edge: any, index: number) =>
            <ArticleCard key={edge?.node.id || index}
                         loading={loading}
                         data={edge?.node}/>
          )}
        </Wrapper.ScrollableListContent>

        <Wrapper.ScrollableListFooter>
          <Button type="button" className="link"
                  onClick={() => onSearch(propertiesDisplay)}>
            {intl.formatMessage(transDefs.explore)}
          </Button>
        </Wrapper.ScrollableListFooter>
      </Wrapper.ScrollableList>

      <EstimatePart className="hp separator inner" />
      <AboutPart className="hp separator" />
      <PrestationsPart className="hp separator inner" />
      <ReviewsPart className="hp separator" />

      <PageFoot background={footBackground}>
        <h2>{intl.formatMessage(transDefs.foot)}</h2>
        <Button type="button" className="link white l-spacing"
                onClick={() => navigate("/contact/")}>
          {intl.formatMessage(actions.contact)}
        </Button>
      </PageFoot>

    </>);

};

export default HomePage;
